/**
 * IndustryMappingPanel — data / state controller.
 *
 * All visual sections are delegated to focused sub-components:
 *   IndustryPickerGate     → loading + no-industry-selected states
 *   IndustryMatrixToolbar  → 2-row control bar
 *   IndustryBulkBar        → bulk map / unmap row
 *   IndustryCountersBar    → scope counters
 *   IndustryMatrixGrid     → full scrollable matrix table
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Building2 } from 'lucide-react';
import { erpToast } from './erp/ERPToast';
import { snapshotJunction } from './IndustryMappingBlocks';
import { IndustryPickerGate }   from './industryMapping/IndustryPickerGate';
import { IndustryMatrixToolbar } from './industryMapping/IndustryMatrixToolbar';
import { IndustryBulkBar }       from './industryMapping/IndustryBulkBar';
import { IndustryCountersBar }   from './industryMapping/IndustryCountersBar';
import { IndustryMatrixGrid }    from './industryMapping/IndustryMatrixGrid';
import { useT } from '../i18n/I18nContext.jsx';
import { API, apiFetch } from '../lib/api';

const MAX_EXPAND = 15;

export default function IndustryMappingPanel({ initialIndustry = null } = {}) {
  const t = useT();

  // ── Server data ──────────────────────────────────────────────────────────
  const [matrix,    setMatrix]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);

  // ── Editable working state ───────────────────────────────────────────────
  const [scopes,   setScopes]   = useState({ Module: {}, SubModule: {}, Screen: {} });
  const [junction, setJunction] = useState({});
  const [pristine, setPristine] = useState({ scopes: null, junction: null });

  // ── UI state ─────────────────────────────────────────────────────────────
  const [search,          setSearch]          = useState('');
  const [expanded,        setExpanded]        = useState(new Set());
  const [industryFilter,  setIndustryFilter]  = useState('');
  const [showOnly,        setShowOnly]        = useState('vertical_common');
  const [entityTypeFilter,setEntityTypeFilter]= useState('all');
  const [selectedIndustry,setSelectedIndustry]= useState(initialIndustry || '');
  const [industryList,    setIndustryList]    = useState([]);

  // Debounced search/filter
  const [debouncedSearch,         setDebouncedSearch]         = useState('');
  const [debouncedIndustryFilter, setDebouncedIndustryFilter] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 250);         return () => clearTimeout(t); }, [search]);
  useEffect(() => { const t = setTimeout(() => setDebouncedIndustryFilter(industryFilter), 250); return () => clearTimeout(t); }, [industryFilter]);

  // Mutation counter for dirty detection (avoids JSON.stringify on 16k objects)
  const [_dirtyCtr, setDirtyCtr] = useState(0);
  const dirty = _dirtyCtr > 0;

  // ── Bootstrap industry list ──────────────────────────────────────────────
  useEffect(() => {
    let autoSelected = false;
    apiFetch(`${API}/uams/industries?activeOnly=true`)
      .then(r => r.json())
      .then(d => {
        const rows     = Array.isArray(d) ? d : (d.data || d.industries || []);
        const filtered = rows.filter(r => r.IndustryCode && r.IndustryCode !== 'SHARED');
        setIndustryList(filtered);
        if (!initialIndustry && filtered.length > 0) {
          autoSelected = true;
          setSelectedIndustry(filtered[0].IndustryCode);
        }
      })
      .catch(() => {})
      .finally(() => { if (!autoSelected) setLoading(false); });
  // eslint-disable-next-line
  }, []);

  // ── Load matrix ──────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!selectedIndustry) { setMatrix(null); return; }
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/uams/industry-matrix?industryCode=${encodeURIComponent(selectedIndustry)}`);
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      setMatrix(d);

      const nextScopes = { Module: {}, SubModule: {}, Screen: {} };
      for (const m  of d.modules)    nextScopes.Module[m.ModuleId]       = m.Scope || 'Vertical';
      for (const s  of d.subModules) nextScopes.SubModule[s.SubModuleId] = s.Scope || 'Vertical';
      for (const sc of d.screens)    nextScopes.Screen[sc.SeqId]         = sc.Scope || 'Vertical';

      const nextJunction = {};
      for (const code of Object.keys(d.junction || {})) {
        nextJunction[code] = {
          Module:    new Set((d.junction[code].Module    || []).map(String)),
          SubModule: new Set((d.junction[code].SubModule || []).map(String)),
          Screen:    new Set((d.junction[code].Screen    || []).map(String)),
        };
      }
      setScopes(nextScopes);
      setJunction(nextJunction);
      setPristine({ scopes: JSON.parse(JSON.stringify(nextScopes)), junction: snapshotJunction(nextJunction) });
      setDirtyCtr(0);
    } catch (e) {
      erpToast.error(`${t('erp.industryMatrix.loadFailed', 'Failed to load matrix')}: ${e.message}`);
    }
    setLoading(false);
  }, [selectedIndustry, t]);

  useEffect(() => { load(); }, [load]);

  // ── Tree derivation ──────────────────────────────────────────────────────
  const tree = useMemo(() => {
    if (!matrix) return [];
    const subsByModule = {};
    for (const s of matrix.subModules) {
      (subsByModule[s.ParentModuleId] = subsByModule[s.ParentModuleId] || []).push(s);
    }
    const scrsBySub = {}, scrsByModNoSub = {};
    for (const sc of matrix.screens) {
      const key = String(sc.SubModuleId || '');
      if (key) (scrsBySub[key] = scrsBySub[key] || []).push(sc);
      else     (scrsByModNoSub[sc.ModuleId] = scrsByModNoSub[sc.ModuleId] || []).push(sc);
    }
    return matrix.modules.map(m => ({
      ...m,
      subModules:    (subsByModule[m.ModuleId] || []).map(s => ({ ...s, screens: scrsBySub[String(s.SubModuleId)] || [] })),
      directScreens: scrsByModNoSub[m.ModuleId] || [],
    }));
  }, [matrix]);

  // ── Filtered tree ────────────────────────────────────────────────────────
  const scopesRef = useRef(scopes);
  useEffect(() => { scopesRef.current = scopes; }, [scopes]);

  const filteredTree = useMemo(() => {
    if (!matrix) return [];
    const q            = debouncedSearch.trim().toLowerCase();
    const currentScopes = scopesRef.current;
    const scopeMatch = (type, id) => {
      if (showOnly === 'all') return true;
      const sc = ((currentScopes[type] || {})[id] || 'Vertical').toLowerCase();
      if (showOnly === 'vertical_common') return sc === 'vertical' || sc === 'common';
      return sc === showOnly;
    };
    return tree.map(m => {
      const matchM      = !q || `${m.ModuleId} ${m.ModuleName}`.toLowerCase().includes(q);
      const moduleScopeOk = scopeMatch('Module', m.ModuleId);
      const subs = m.subModules.map(s => {
        const matchS = !q || `${s.SubModuleId} ${s.SubModuleName}`.toLowerCase().includes(q);
        const scrs   = s.screens.filter(sc => (!q || `${sc.SeqId} ${sc.MenuTitle}`.toLowerCase().includes(q)) && scopeMatch('Screen', sc.SeqId));
        const subScopeOk = scopeMatch('SubModule', s.SubModuleId);
        if (!matchS && !matchM && scrs.length === 0) return null;
        if (!subScopeOk && scrs.length === 0 && showOnly !== 'all') return null;
        if (entityTypeFilter === 'module') return null;
        if (entityTypeFilter === 'screen') return scrs.length > 0 ? { ...s, screens: scrs } : null;
        return { ...s, screens: entityTypeFilter === 'submodule' ? [] : scrs };
      }).filter(Boolean);
      const direct = m.directScreens.filter(sc => (!q || `${sc.SeqId} ${sc.MenuTitle}`.toLowerCase().includes(q)) && scopeMatch('Screen', sc.SeqId));
      if (entityTypeFilter === 'submodule' || entityTypeFilter === 'screen') {
        if (!subs.length && !direct.length) return null;
        return { ...m, subModules: subs, directScreens: direct, _hideModuleRow: true };
      }
      if (!matchM && !subs.length && !direct.length) return null;
      if (showOnly !== 'all' && !moduleScopeOk && !subs.length && !direct.length) return null;
      return { ...m, subModules: subs, directScreens: direct };
    }).filter(Boolean);
  }, [tree, debouncedSearch, showOnly, matrix, entityTypeFilter]);

  const visibleIndustries = useMemo(() => {
    if (!matrix) return [];
    const q = debouncedIndustryFilter.trim().toLowerCase();
    if (!q) return matrix.industries;
    return matrix.industries.filter(i => `${i.IndustryCode} ${i.IndustryName} ${i.Category || ''}`.toLowerCase().includes(q));
  }, [matrix, debouncedIndustryFilter]);

  // ── Cell helpers ─────────────────────────────────────────────────────────
  const isMappedTo = (industryCode, type, id) =>
    !!(junction[industryCode]?.[type]?.has(String(id)));

  const toggleCommon = useCallback((type, id) => {
    setScopes(prev => {
      const current = (prev[type] || {})[id] || 'Vertical';
      return { ...prev, [type]: { ...prev[type], [id]: current === 'Common' ? 'Vertical' : 'Common' } };
    });
    setDirtyCtr(c => c + 1);
  }, []);

  const toggleIndustryCell = useCallback((industryCode, type, id) => {
    setJunction(prev => {
      const next = { ...prev };
      if (!next[industryCode]) {
        next[industryCode] = { Module: new Set(), SubModule: new Set(), Screen: new Set() };
      } else {
        next[industryCode] = {
          Module:    new Set(next[industryCode].Module),
          SubModule: new Set(next[industryCode].SubModule),
          Screen:    new Set(next[industryCode].Screen),
        };
      }
      const k = String(id);
      const set = next[industryCode][type];
      if (set.has(k)) set.delete(k); else set.add(k);
      return next;
    });
    setDirtyCtr(c => c + 1);
  }, []);

  // Delegated click handler — zero per-cell function props
  const handleTableClick = useCallback((e) => {
    const td = e.target.closest('[data-cell]');
    if (!td) return;
    const { cell, entityType, entityId, industryCode } = td.dataset;
    if (cell === 'common')   toggleCommon(entityType, entityId);
    else if (cell === 'industry' && industryCode) toggleIndustryCell(industryCode, entityType, entityId);
  }, [toggleCommon, toggleIndustryCell]);

  // ── Bulk toggle (called from IndustryBulkBar) ────────────────────────────
  const handleBulkToggle = useCallback((industryCode, allMapped, filteredRows) => {
    setJunction(prev => {
      const next = { ...prev };
      if (!next[industryCode]) next[industryCode] = { Module: new Set(), SubModule: new Set(), Screen: new Set() };
      else next[industryCode] = {
        Module:    new Set(next[industryCode].Module),
        SubModule: new Set(next[industryCode].SubModule),
        Screen:    new Set(next[industryCode].Screen),
      };
      const action = allMapped ? 'delete' : 'add';
      filteredRows.forEach(m => {
        if (!m._hideModuleRow) next[industryCode].Module[action](String(m.ModuleId));
        (m.subModules || []).forEach(s => {
          next[industryCode].SubModule[action](String(s.SubModuleId));
          (s.screens || []).forEach(sc => next[industryCode].Screen[action](String(sc.SeqId)));
        });
        (m.directScreens || []).forEach(sc => next[industryCode].Screen[action](String(sc.SeqId)));
      });
      return next;
    });
    setDirtyCtr(c => c + 1);
  }, []);

  // ── Expand / collapse ─────────────────────────────────────────────────────
  const expandAll   = () => { if (filteredTree.length > MAX_EXPAND) return; setExpanded(new Set(filteredTree.map(m => m.ModuleId))); };
  const collapseAll = () => setExpanded(new Set());
  const toggleExpand = (mid) => setExpanded(prev => { const next = new Set(prev); next.has(mid) ? next.delete(mid) : next.add(mid); return next; });

  // ── Revert ────────────────────────────────────────────────────────────────
  const revert = () => {
    if (!pristine.scopes) return;
    setScopes(JSON.parse(JSON.stringify(pristine.scopes)));
    const restoredJ = {};
    for (const code of Object.keys(pristine.junction)) {
      restoredJ[code] = {
        Module:    new Set(pristine.junction[code].Module),
        SubModule: new Set(pristine.junction[code].SubModule),
        Screen:    new Set(pristine.junction[code].Screen),
      };
    }
    setJunction(restoredJ);
    setDirtyCtr(0);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    try {
      const derivedScopes = { Module: {}, SubModule: {}, Screen: {} };
      for (const type of ['Module', 'SubModule', 'Screen']) {
        for (const id of Object.keys(scopes[type] || {})) {
          if (scopes[type][id] === 'Common') { derivedScopes[type][id] = 'Common'; continue; }
          let count = 0;
          for (const code of Object.keys(junction)) {
            if (junction[code]?.[type]?.has(String(id))) count++;
          }
          derivedScopes[type][id] = count >= 2 ? 'Cross' : 'Vertical';
        }
      }
      const payloadJunction = {};
      for (const code of Object.keys(junction)) {
        payloadJunction[code] = {
          modules:    Array.from(junction[code].Module).map(v => parseInt(v, 10)).filter(Number.isFinite),
          subModules: Array.from(junction[code].SubModule),
          screens:    Array.from(junction[code].Screen).map(v => parseInt(v, 10)).filter(Number.isFinite),
        };
      }
      const r = await apiFetch(`${API}/uams/industry-matrix`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ scopes: derivedScopes, junction: payloadJunction }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      erpToast.success(`${t('erp.industryMatrix.saved', 'Matrix saved')} · ${d.scopeUpdates} scope updates · ${d.junctionRows} mapping rows`);
      await load();
    } catch (e) {
      erpToast.error(`${t('erp.industryMatrix.saveFailed', 'Save failed')}: ${e.message}`);
    }
    setSaving(false);
  };

  // ── Counters ──────────────────────────────────────────────────────────────
  const counters = useMemo(() => {
    let common = 0, cross = 0, vertical = 0;
    for (const type of ['Module', 'SubModule', 'Screen']) {
      for (const sc of Object.values(scopes[type] || {})) {
        const v = (sc || 'Vertical').toLowerCase();
        if (v === 'common') common++; else if (v === 'cross') cross++; else vertical++;
      }
    }
    return { common, cross, vertical };
  }, [scopes]);

  const autoScopeMap = useMemo(() => {
    const map = {};
    for (const type of ['Module', 'SubModule', 'Screen']) {
      for (const [id, scope] of Object.entries(scopes[type] || {})) {
        if (scope === 'Common') { map[`${type}:${id}`] = 'Common'; continue; }
        let count = 0;
        for (const code of Object.keys(junction)) {
          if (junction[code]?.[type]?.has(String(id))) count++;
        }
        map[`${type}:${id}`] = count >= 2 ? 'Cross' : 'Vertical';
      }
    }
    return map;
  }, [scopes, junction]);

  // ── Early-return states (via IndustryPickerGate) ──────────────────────────
  if (!selectedIndustry) {
    return (
      <IndustryPickerGate
        bootstrapLoading={loading}
        selectedIndustry={selectedIndustry}
        industryList={industryList}
        onSelect={setSelectedIndustry}
      />
    );
  }

  if (loading || !matrix) {
    return (
      <div data-testid="industry-mapping-panel-loading"
        style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
        {t('erp.industryMatrix.loadingMatrix', 'Loading matrix for')} <strong>{selectedIndustry}</strong>…
      </div>
    );
  }

  // ── Full matrix render ────────────────────────────────────────────────────
  return (
    <div data-testid="industry-mapping-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header explainer */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', borderRadius: 6,
        background: 'var(--erp-grid-row-hover, #eff6ff)',
        border: '1px solid var(--erp-border, #bfdbfe)',
        fontSize: 12, color: '#1e40af', lineHeight: 1.55
      }}>
        <Building2 size={14} />
        <div>
          <strong>{t('erp.industryMatrix.title', 'Industry × Module Matrix')}</strong>
          {' · '}{t('erp.industryMatrix.explainer',
            'Rows = Modules/SubModules/Screens. Columns = active industries. The first Common column makes an entity available to every industry automatically.'
          )}
        </div>
      </div>

      <IndustryMatrixToolbar
        selectedIndustry={selectedIndustry}  industryList={industryList}  onSelectIndustry={setSelectedIndustry}
        search={search}                       onSearch={setSearch}
        industryFilter={industryFilter}       onIndustryFilter={setIndustryFilter}
        entityTypeFilter={entityTypeFilter}   onEntityTypeFilter={setEntityTypeFilter}
        showOnly={showOnly}                   onShowOnly={setShowOnly}
        filteredCount={filteredTree.length}
        dirty={dirty}   saving={saving}   loading={loading}
        expandAll={expandAll}   collapseAll={collapseAll}   revert={revert}   onReload={load}
        MAX_EXPAND={MAX_EXPAND}
      />

      <IndustryBulkBar
        visibleIndustries={visibleIndustries}
        filteredTree={filteredTree}
        junction={junction}
        onBulkToggle={handleBulkToggle}
      />

      <IndustryCountersBar
        counters={counters}
        matrix={matrix}
        visibleIndustries={visibleIndustries}
        dirty={dirty}
      />

      <IndustryMatrixGrid
        filteredTree={filteredTree}
        visibleIndustries={visibleIndustries}
        expanded={expanded}
        scopes={scopes}
        autoScopeMap={autoScopeMap}
        isMappedTo={isMappedTo}
        handleTableClick={handleTableClick}
        toggleExpand={toggleExpand}
      />

      <div style={{ fontSize: 12, color: '#64748b', padding: '4px 12px' }}>
        {t('erp.industryMatrix.tip',
          'Tip: hover a column header for full industry name. Click Common to make an entity available to every industry. Map ≥2 industry cells to auto-tag a row as Cross.'
        )}
      </div>
    </div>
  );
}
